// search-modal.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './search-modal.css';
import oncoRiskConfig, { getApiEndpoint, getItemById, getItemColor } from '../../config/oncorisk-config';

function SearchModal({ searchTerm, isOpen, onClose, onSelect }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchableTypes, setSearchableTypes] = useState(() => {
    const knowledgeGroup = oncoRiskConfig.groups.find(g => g.id === 'knowledge');
    return new Set(knowledgeGroup ? knowledgeGroup.items : []);
  });
  const modalRef = useRef(null);
  const fetchTimeoutRef = useRef(null);
  const [showContent, setShowContent] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  // Memoize fetch function. It now accepts the types to search.
  const fetchResults = useCallback(async (term, typesToSearch) => {
    // Do not search if there's no term or no types are selected
    if (!term || typesToSearch.size === 0) {
      setResults([]);
      return;
    }
    
    setLoading(true);
    // We don't clear results here, to prevent blinking
    try {
      // **MODIFIED**: Append selected item types as a query parameter
      const typesParam = Array.from(typesToSearch).join(',');
      const url = `${getApiEndpoint('search')}?term=${encodeURIComponent(term)}&itemTypes=${encodeURIComponent(typesParam)}`;
      
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`Search failed: ${resp.status}`);
      const data = await resp.json();
      setResults(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Search error', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []); // dependencies are empty because it's a stable function definition

  // Effect to handle search term and filter changes
  useEffect(() => {
    if (!isOpen || !searchTerm) {
        setResults([]); // Clear results if modal is closed or search term is empty
        return;
    }
    
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    
    // Set new timeout for debouncing
    fetchTimeoutRef.current = setTimeout(() => {
      // **MODIFIED**: Pass the current searchableTypes to the fetch function
      fetchResults(searchTerm, searchableTypes);
    }, 300);
    
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  // **MODIFIED**: Rerun this effect when filters change
  }, [searchTerm, isOpen, searchableTypes, fetchResults]); 
  
  // Effect to handle animation and click outside (no changes here)
  useEffect(() => {
    if (isOpen) {
      setShowContent(true);
      const animTimer = setTimeout(() => setAnimateIn(true), 10);
      return () => clearTimeout(animTimer);
    } else {
      setAnimateIn(false);
      const timer = setTimeout(() => setShowContent(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Effect for click outside (separated for clarity, no logic change)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        const searchInput = document.querySelector('.OCRP-header-search');
        if (searchInput && searchInput.contains(event.target)) {
          return;
        }
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen && !showContent) return null;

  const handleSelect = (item) => {
    if (onSelect) onSelect(item);
  };

  const toggleSearchableType = (type) => {
    setSearchableTypes(prev => {
      const newTypes = new Set(prev);
      if (newTypes.has(type)) {
        newTypes.delete(type);
      } else {
        newTypes.add(type);
      }
      return newTypes;
    });
  };

  return (
    <div 
      ref={modalRef}
      className={`OCRP-search-modal ${animateIn ? 'OCRP-search-modal-show' : 'OCRP-search-modal-hide'}`}
    >
      {/* Results area with fixed height */}
      <div className="OCRP-search-results-container">
        {loading && !results.length ? (
          <div className="OCRP-search-loading">Searching...</div>
        ) : results.length === 0 ? (
          <div className="OCRP-search-empty">No results for the selected filters</div>
        ) : (
          <div className="OCRP-search-results">
            {results.map((item) => {
              const type = item.itemType || '';
              const cfg = getItemById(type) || {};
              const color = getItemColor(type);
              return (
                <div
                  key={`${type}-${item.id}`}
                  className="OCRP-search-item"
                  onClick={() => handleSelect(item)}
                >
                  <div
                    className="OCRP-search-item-icon"
                    style={{
                      background: `linear-gradient(to bottom, ${color}, ${color.replace(/,\s*[\d.]+\)/, ',0.2)')})`
                    }}
                  >
                    <i className={`fas fa-${cfg.icon || 'search'}`}></i>
                  </div>
                  <div className="OCRP-search-item-info">
                    <div className="OCRP-search-item-name">{item.name || item.id}</div>
                    <div className="OCRP-search-item-type">{item.itemTypeDisplayName || type}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Filter section - always visible */}
      <div className="OCRP-search-filters">
        {oncoRiskConfig.groups.find(g => g.id === 'knowledge')?.items.map(type => {
          const itemConfig = getItemById(type);
          if (!itemConfig) return null;
          return (
            <div key={type} className="OCRP-search-filter-item">
              <input
                type="checkbox"
                id={`filter-${type}`}
                checked={searchableTypes.has(type)}
                onChange={() => toggleSearchableType(type)}
              />
              <label htmlFor={`filter-${type}`} title={itemConfig.label}>
                <i className={`fas fa-${itemConfig.icon}`}></i>
              </label>
            </div>
          );
        })}
      </div>
      
      {/* Loading indicator overlay */}
      {loading && results.length > 0 && (
        <div className="OCRP-search-loading-overlay">
          <div className="OCRP-search-loading-spinner"></div>
        </div>
      )}
    </div>
  );
}

export default SearchModal;